import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import Papa from 'papaparse';

interface AnalyticsState {
  loading: boolean;
  error: string | null;
  data: {
    bidPerformance: any[];
    contractValue: any[];
    agencyDistribution: any[];
    complianceMetrics: any[];
  };
  fetchAnalytics: (dateRange: string) => Promise<void>;
  exportData: (format: 'pdf' | 'csv' | 'excel') => Promise<void>;
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  loading: false,
  error: null,
  data: {
    bidPerformance: [],
    contractValue: [],
    agencyDistribution: [],
    complianceMetrics: [],
  },

  fetchAnalytics: async (dateRange: string) => {
    set({ loading: true, error: null });
    try {
      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 1000));

      set({
        data: {
          bidPerformance: [
            { month: 'Jan', success: 65, total: 100 },
            { month: 'Feb', success: 72, total: 100 },
            { month: 'Mar', success: 68, total: 100 },
          ],
          contractValue: [
            { month: 'Jan', value: 1200000 },
            { month: 'Feb', value: 1500000 },
            { month: 'Mar', value: 1800000 },
          ],
          agencyDistribution: [
            { agency: 'DoD', percentage: 45, contracts: 28 },
            { agency: 'DHS', percentage: 25, contracts: 15 },
            { agency: 'HHS', percentage: 20, contracts: 12 },
            { agency: 'Others', percentage: 10, contracts: 6 },
          ],
          complianceMetrics: [
            { category: 'Documentation', score: 98, issues: 0 },
            { category: 'Technical', score: 85, issues: 2 },
            { category: 'Past Performance', score: 95, issues: 0 },
            { category: 'Pricing', score: 75, issues: 3 },
          ],
        },
        loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      set({ error: 'Failed to load analytics data', loading: false });
      toast.error('Failed to load analytics data');
    }
  },

  exportData: async (format: 'pdf' | 'csv' | 'excel') => {
    const { data } = get();
    
    try {
      if (format === 'csv') {
        // Export as CSV
        const csvData = Papa.unparse({
          fields: ['Category', 'Value', 'Change'],
          data: [
            ['Bid Success Rate', '68%', '+4.2%'],
            ['Total Contract Value', '$12.4M', '+15.3%'],
            ['Active Proposals', '156', '+12.5%'],
            ['Compliance Score', '94%', '+2.1%'],
          ],
        });

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        // Export as PDF
        const doc = new jsPDF();
        
        // Add title
        doc.setFontSize(16);
        doc.text('Analytics Report', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 22);

        // Add performance metrics
        doc.setFontSize(12);
        doc.text('Performance Metrics', 14, 35);
        
        const metrics = [
          ['Metric', 'Value', 'Change'],
          ['Bid Success Rate', '68%', '+4.2%'],
          ['Total Contract Value', '$12.4M', '+15.3%'],
          ['Active Proposals', '156', '+12.5%'],
          ['Compliance Score', '94%', '+2.1%'],
        ];

        (doc as any).autoTable({
          startY: 40,
          head: [metrics[0]],
          body: metrics.slice(1),
          theme: 'grid',
        });

        // Save the PDF
        doc.save(`analytics_${new Date().toISOString().split('T')[0]}.pdf`);
      } else {
        // Excel export would typically use a library like xlsx
        toast.error('Excel export coming soon');
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export analytics data');
      throw error;
    }
  },
}));